import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { apiClient } from "../lib/api";
import { useI18n } from "../lib/i18n";
import {
  ShieldCheck, Shield, Truck, CreditCard, Handshake, Stethoscope,
  ExternalLink, Phone, BadgeCheck, ArrowRight, Loader2, Info,
  FileSearch,
} from "lucide-react";
import CartalVerifiedPartnersStrip from "../components/CartalVerifiedPartnersStrip";

const ICONS = { ShieldCheck, Shield, Truck, CreditCard, Handshake, Stethoscope, FileSearch };

/* Phase 2F — map a service route category → matching CARTAL Business
   category keys. Used to surface verified in-house partners in the same
   view users are browsing external providers on. */
const BUSINESS_CATEGORIES_BY_ROUTE = {
  insurance:               ["insurance_company", "insurance_broker"],
  financing:               ["bank", "finance_company", "financing_platform", "leasing_company", "bnpl_service", "fintech"],
  periodic_inspection:     ["periodic_inspection", "inspection_center"],
  pre_purchase_inspection: ["inspection_center"],
  delivery:                ["transport_company", "recovery_towing"],
  workshop:                ["workshop"],
  car_wash:                ["car_wash"],
  parts:                   ["parts_store", "tire_battery_shop", "salvage"],
  tire_battery:            ["tire_battery_shop"],
  banks:                   ["bank"],
  fintech:                 ["fintech", "payment_provider"],
  bnpl:                    ["bnpl_service"],
  // iter264 — Mojaz is an official government service, not a CARTAL
  // business type. Registered here with an empty partner list so
  // `isLegacyBiz` becomes true and the page reads from the working
  // legacy `/providers/vehicle_history` endpoint instead of falling
  // through to `/other-services/*` which returns "unknown category".
  vehicle_history:         [],
  // iter272 — Ownership transfer is a government/broker directory
  // (Absher, Wateen, MOI…) with no matching CARTAL business category
  // yet. Same treatment as `vehicle_history`: empty partner list so
  // `isLegacyBiz=true` routes to `/providers/ownership_transfer`
  // (which returns 200 with the real providers) instead of falling
  // through to `/other-services/*` which 404s "unknown category" and
  // breaks both AR and EN UIs.
  ownership_transfer:      [],
};

/* iter251 — "خدمات أخرى" sub-categories are pre-launch: keep the page
   in a strict empty-state (no external providers, no verified CARTAL
   partners strip) until we onboard vetted vendors. Also carry the
   correct Arabic/English meta so we never fall through to the backend
   fallback "Unknown category" label. */
const OTHER_SERVICES_META = {
  delivery: {
    icon: "Truck",
    title_ar: "شركات ومزوّدو خدمات نقل وشحن المركبات",
    title_en: "Vehicle transport & shipping providers",
    subtitle_ar: "شحن سيارتك بين المدن أو من/إلى الميناء عبر مزوّدين معتمدين.",
    subtitle_en: "Ship your car across cities or through ports with accredited operators.",
  },
  car_wash: {
    icon: "Handshake",
    title_ar: "مراكز العناية بالسيارات",
    title_en: "Car-care centres",
    subtitle_ar: "غسيل، بوليش، حماية طلاء، تنظيف داخلي — من متخصّصين معتمدين.",
    subtitle_en: "Wash, polish, paint protection, interior detailing — by accredited specialists.",
  },
  workshop: {
    icon: "ShieldCheck",
    title_ar: "مراكز الصيانة الشاملة",
    title_en: "Comprehensive service centres",
    subtitle_ar: "ورش صيانة معتمدة لكل الأنواع — ميكانيكا، كهرباء، سمكرة، ودهان.",
    subtitle_en: "Accredited workshops covering all trades — mechanical, electrical, body, and paint.",
  },
};

/**
 * ProviderDirectoryPage — generic page that lists third-party service
 * providers for a single category (inspection / insurance / delivery /
 * financing / ownership_transfer).  CARTAL is purely a broker here:
 *   - Card shows logo (initials), name, one-line description, tags, phone.
 *   - The CTA button is an outbound link to the provider's official site.
 *   - NO prices, NO booking forms, NO multi-step wizards — everything
 *     transactional happens on the provider's own website.
 */
export default function ProviderDirectoryPage({ category }) {
  // category can come as a prop (one page per route) or via :category param.
  const params = useParams();
  const cat = category || params.category;
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  // iter289 — Verified CARTAL partners count for the mapped business
  // categories of this route. Used to (a) render the verified-partners
  // strip on the pre-launch "خدمات أخرى" sub-routes (/workshop,
  // /car-wash, /delivery) which previously hid it, and (b) hide the
  // "لا يوجد شركاء بعد" empty state whenever real verified partners
  // exist even if the admin-curated `other_services_providers` list is
  // still empty. Zero data duplication — reads straight from
  // `platform_businesses` via the shared public endpoint.
  const [verifiedCount, setVerifiedCount] = useState(0);

  useEffect(() => {
    setData(null);
    setErr(null);
    setVerifiedCount(0);
    // iter251/252/258 — For system + admin-added "خدمات أخرى"
    // sub-categories, pull from /other-services (never from the
    // legacy /providers endpoint which 404s and leaks English).
    const isSystem = Object.prototype.hasOwnProperty.call(OTHER_SERVICES_META, cat);
    const isLegacyBiz = Object.prototype.hasOwnProperty.call(BUSINESS_CATEGORIES_BY_ROUTE, cat);
    const tryOtherServices = async () => {
      const [{ data: prov }, cats] = await Promise.all([
        apiClient.get(`/other-services/providers/${cat}`),
        apiClient.get(`/other-services/categories`).then((r) => r.data.items || []).catch(() => []),
      ]);
      const dyn = cats.find((c) => c.id === cat);
      const meta = OTHER_SERVICES_META[cat] || (dyn
        ? {
            icon: "Handshake",
            title_ar: dyn.ar_title,
            title_en: dyn.en_title,
            subtitle_ar: dyn.ar_desc,
            subtitle_en: dyn.en_desc,
          }
        : null);
      if (!meta) throw new Error("unknown category");
      const items = (prov?.items || []).map((p) => ({
        id: p.id,
        name_ar: p.name_ar,
        name_en: p.name_en,
        desc_ar: p.desc_ar,
        desc_en: p.desc_en,
        phone: p.phone,
        website: p.external_url,
        tags: p.tags || [],
        verified: p.accredited,
      }));
      setData({ meta, items });
    };
    if (isSystem) {
      tryOtherServices().catch(() =>
        setData({ meta: OTHER_SERVICES_META[cat], items: [] })
      );
      return;
    }
    if (!isLegacyBiz) {
      // Likely an admin-added activity type — treat as other-services
      tryOtherServices().catch((e) => setErr(e.response?.data?.detail || e.message));
      return;
    }
    apiClient
      .get(`/providers/${cat}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, [cat]);

  // iter289 — Count verified CARTAL partners in the business categories
  // mapped to this route. Runs for EVERY route that has a mapping,
  // including the pre-launch "خدمات أخرى" sub-routes so the verified
  // strip finally appears there too. Silent no-op when there is no
  // mapping (e.g., ownership_transfer / vehicle_history / admin-added
  // dynamic categories).
  useEffect(() => {
    const cats = BUSINESS_CATEGORIES_BY_ROUTE[cat];
    if (!cats || cats.length === 0) { setVerifiedCount(0); return; }
    let cancelled = false;
    Promise.all(cats.map((c) =>
      apiClient.get("/platform/public/companies", { params: { category: c, limit: 1 } })
        .then(({ data }) => Number(data?.total || 0))
        .catch(() => 0)
    )).then((counts) => {
      if (cancelled) return;
      setVerifiedCount(counts.reduce((a, b) => a + b, 0));
    });
    return () => { cancelled = true; };
  }, [cat]);

  if (err) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 text-[#525252]">
        {String(err)}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 inline-flex items-center gap-2 text-[#6b6b6b]">
        <Loader2 className="w-4 h-4 animate-spin" /> {lang === "ar" ? "جاري التحميل..." : "Loading..."}
      </div>
    );
  }

  const Icon = ICONS[data.meta?.icon] || ShieldCheck;
  // iter251/252 — For the 3 pre-launch "خدمات أخرى" sub-categories:
  //   * hide the platform-wide CARTAL verified-partners strip so nothing
  //     from the general businesses catalogue leaks in
  //   * use the payload we already fetched from /other-services/providers
  //     (Admin-curated list) as the source of truth. If it's empty the
  //     shared "لا يوجد شركاء بعد" empty state renders.
  //   * stamp Arabic labels so "Unknown category" can never surface.
  const isOtherServicesCat = Object.prototype.hasOwnProperty.call(OTHER_SERVICES_META, cat)
    || !Object.prototype.hasOwnProperty.call(BUSINESS_CATEGORIES_BY_ROUTE, cat);
  const effectiveItems = data.items || [];
  const meta = isOtherServicesCat ? OTHER_SERVICES_META[cat] : (data.meta || {});
  const title = lang === "ar" ? meta.title_ar : meta.title_en;
  const subtitle = lang === "ar" ? meta.subtitle_ar : meta.subtitle_en;
  const audience = lang === "ar" ? meta.audience_ar : meta.audience_en;

  return (
    <div
      className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12"
      data-testid={`provider-directory-${cat}`}
    >
      <div className="flex items-start gap-4 max-w-3xl mb-2">
        <span
          className="inline-flex items-center justify-center w-12 h-12 rounded-2xl shrink-0"
          style={{
            backgroundColor: "rgba(201,165,92,0.10)",
            border: "1px solid rgba(201,165,92,0.45)",
          }}
        >
          <Icon className="w-6 h-6 text-[#8a6f2e]" />
        </span>
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="overline overline-gold">{lang === "ar" ? "دليل كارتال" : "CARTAL Directory"}</span>
            {audience && (
              <span
                className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
                style={{ backgroundColor: "#0a0a0a", color: "#d4ae6a" }}
                data-testid="directory-audience"
              >
                {audience}
              </span>
            )}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-medium text-[#0a0a0a] leading-tight">
            {title}
          </h1>
        </div>
      </div>
      <p className="text-[#525252] max-w-3xl text-base leading-relaxed mb-8">{subtitle}</p>

      {/* Broker disclaimer */}
      <div
        className="flex items-start gap-3 rounded-2xl p-4 mb-10"
        style={{ backgroundColor: "#fafaf7", border: "1px solid rgba(201,165,92,0.30)" }}
        data-testid="broker-disclaimer"
      >
        <Info className="w-4 h-4 text-[#8a6f2e] mt-0.5 shrink-0" />
        <p className="text-xs text-[#525252] leading-relaxed">
          {lang === "ar"
            ? "كارتال منصة وسيطة وتعرض الجهات المقدّمة للخدمة فقط. الأسعار والحجز والدفع تتم مباشرة على موقع مزوّد الخدمة. كارتال ليست طرفاً في العقد بينك وبين المزوّد."
            : "CARTAL is purely a directory. Pricing, booking, and payment happen on the provider's own website. CARTAL is not a party to any contract between you and the provider."}
        </p>
      </div>

      {/* iter289 — Verified CARTAL partners strip. Renders for every
          route with a BUSINESS_CATEGORIES_BY_ROUTE mapping — including
          the pre-launch "خدمات أخرى" sub-routes (/workshop, /car-wash,
          /delivery) that previously hid it. The strip itself is a
          silent no-op when there are no verified partners, so pages
          with only admin-curated content still render exactly as
          before. Zero-duplication: reads straight from platform_businesses. */}
      {BUSINESS_CATEGORIES_BY_ROUTE[cat] && BUSINESS_CATEGORIES_BY_ROUTE[cat].length > 0 && (
        <CartalVerifiedPartnersStrip categories={BUSINESS_CATEGORIES_BY_ROUTE[cat]} />
      )}

      {/* Provider grid — or empty state if no providers are visible yet.
          iter289 — the empty state also stays hidden when at least one
          verified CARTAL partner exists in the mapped business
          categories, so "لا يوجد شركاء بعد" never contradicts the
          verified strip rendered above. */}
      {effectiveItems.length === 0 && verifiedCount === 0 ? (
        <div
          className="rounded-3xl border-2 border-dashed border-[#c9a55c]/50 bg-[#fbf8ee]/50 p-10 sm:p-14 text-center"
          data-testid="provider-empty-state"
        >
          <span
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
            style={{ backgroundColor: "rgba(201,165,92,0.15)", border: "1.5px solid rgba(201,165,92,0.55)" }}
          >
            <Handshake className="w-8 h-8 text-[#8a6f2e]" />
          </span>
          <h3 className="font-display text-2xl sm:text-3xl font-semibold text-[#0a0a0a] mb-2">
            {lang === "ar" ? "لا يوجد شركاء بعد" : "No partners yet"}
          </h3>
          <p className="text-sm text-[#525252] max-w-lg mx-auto leading-relaxed mb-6">
            {lang === "ar"
              ? "نستقبل حالياً طلبات تسجيل الشركاء الرسميين. كن من أوائل الشركاء المعتمدين في هذه الفئة على كارتال."
              : "We're currently onboarding official partners. Be among the first accredited providers in this category on CARTAL."}
          </p>
          <Link
            to={`/join?intent=partner&activity=${encodeURIComponent(cat)}`}
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold shadow-lg hover:scale-[1.03] transition-transform"
            style={{ background: "linear-gradient(135deg,#d4b56e,#c9a55c,#8a6f2e)", color: "#0a0a0a" }}
            data-testid="provider-empty-cta"
          >
            {lang === "ar" ? "سجّل كشريك معتمد" : "Register as a partner"}
            <ArrowRight className="w-4 h-4 rtl:rotate-180" />
          </Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="provider-grid">
          {effectiveItems.map((p) => (
            <ProviderCard key={p.id} p={p} lang={lang} />
          ))}
        </div>
      )}

      {/* Bottom hint linking back to owner-journey */}
      <div className="mt-14 text-center">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-[#8a6f2e] hover:text-[#5a4615] font-semibold"
        >
          {lang === "ar" ? "العودة للصفحة الرئيسية" : "Back to homepage"}
          <ArrowRight className="w-4 h-4 rtl:rotate-180" />
        </Link>
      </div>
    </div>
  );
}

function ProviderCard({ p, lang }) {
  const name = lang === "ar" ? p.name_ar : p.name_en;
  const desc = lang === "ar" ? p.desc_ar : p.desc_en;
  const tags = lang === "ar" ? p.tags_ar : p.tags_en;
  return (
    <article
      className="rounded-2xl border-2 border-[#0a0a0a]/8 bg-white overflow-hidden hover:border-[#c9a55c] hover:shadow-md transition-all flex flex-col"
      data-testid={`provider-card-${p.id}`}
    >
      <div className="p-5 flex items-start gap-4 border-b border-[#0a0a0a]/5">
        <span
          className="inline-flex items-center justify-center w-14 h-14 rounded-2xl shrink-0 font-display text-2xl font-bold"
          style={{
            backgroundColor: `${p.color}12`,
            border: `1.5px solid ${p.color}40`,
            color: p.color,
          }}
          aria-hidden
        >
          {p.logo_initials}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-lg sm:text-xl font-semibold text-[#0a0a0a] leading-tight">
            {name}
          </h3>
          {p.official && (
            <span
              className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded"
              style={{ backgroundColor: "#0a4f3a", color: "#ffffff" }}
              data-testid={`provider-official-${p.id}`}
            >
              <BadgeCheck className="w-3 h-3" /> {lang === "ar" ? "جهة رسمية معتمدة" : "Officially accredited"}
            </span>
          )}
        </div>
      </div>

      <div className="p-5 flex-1 flex flex-col">
        <p className="text-sm text-[#3a3a3a] leading-relaxed flex-1">{desc}</p>

        {tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-4">
            {tags.map((t, i) => (
              <span
                key={i}
                className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#0a0a0a]/15 text-[#525252]"
              >
                {t}
              </span>
            ))}
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <a
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-colors"
            style={{ backgroundColor: p.color, color: "#ffffff" }}
            data-testid={`provider-visit-${p.id}`}
          >
            {lang === "ar" ? (p.cta_ar || "ابدأ الخدمة") : (p.cta_en || "Start service")}
            <ExternalLink className="w-3.5 h-3.5" style={{ color: "#ffffff" }} />
          </a>
          {p.phone && (
            <a
              href={`tel:+966${String(p.phone).replace(/^0+/, "")}`}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold border border-[#0a0a0a]/15 text-[#0a0a0a] hover:border-[#c9a55c] transition-colors"
              data-testid={`provider-phone-${p.id}`}
              dir="ltr"
            >
              <Phone className="w-3 h-3" /> {p.phone}
            </a>
          )}
        </div>
      </div>
    </article>
  );
}
